// Financial-health insights: margin KPIs, ratio trends over time with period
// filters, and an exportable report.
import React, { useRef, useMemo, useState } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { BarChart, LineChart, HBarChart, Legend } from './charts';
import { computeInsights, openingAsAt, INDICATORS, fmtMoney, fmtMoneyShort, fmtPct, fmtTimes, healthColour } from './insights';
import { filterJournals } from './computeFromJournals';

const GREEN = '#3d7a52', RUST = '#b3623d', GOLD = '#c8a24e', BLUE = '#3d567a';
const TICK_PCT = (v) => (v * 100).toFixed(0) + '%';
const TICK_TIMES = (v) => v.toFixed(1) + '×';

function isoToday() { return new Date().toISOString().slice(0, 10); }
function dayBefore(iso) { if (!iso) return ''; const d = new Date(iso); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); }

// Rolling windows are anchored to the latest transaction (so they show recent
// DATA, not an empty window relative to today when the books are in arrears).
function rangeFor(period, project, cf, ct, anchor) {
  const a = anchor || isoToday();
  const back = (n) => { const d = new Date(a); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
  if (period === '30') return { from: back(30), to: a, label: 'Last 30 days' };
  if (period === '60') return { from: back(60), to: a, label: 'Last 60 days' };
  if (period === '90') return { from: back(90), to: a, label: 'Last 3 months' };
  if (period === 'year') return { from: (project && project.yearStart) || '', to: (project && project.yearEnd) || '', label: 'This year' };
  if (period === 'older') return { from: '', to: dayBefore(project && project.yearStart), label: 'Before this year' };
  if (period === 'custom') return { from: cf || '', to: ct || '', label: 'Custom range' };
  return { from: '', to: '', label: 'All time' };
}

function KpiCard({ label, value, sub, color }) {
  return (
    <div style={{ flex: '1 1 140px', minWidth: 140, background: '#fff', border: '1px solid #eceae4', borderRadius: 8, padding: '12px 14px' }}>
      <div style={{ fontSize: 10, color: '#9a9488', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || '#1a1a2e', marginTop: 4 }}>{value}</div>
      {sub != null && <div style={{ fontSize: 11, color: '#9a9488', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Panel({ title, subtitle, right, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #eceae4', borderRadius: 8, padding: 14, flex: '1 1 320px', minWidth: 300 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e' }}>{title}</div>
          {subtitle && <div style={{ fontSize: 10, color: '#9a9488', marginTop: 1 }}>{subtitle}</div>}
        </div>
        {right}
      </div>
      <div style={{ marginTop: 8 }}>{children}</div>
    </div>
  );
}

function trendSeries(indicator, monthly) {
  const v = (k) => monthly.map((x) => x[k]);
  if (indicator === 'margins') {
    return {
      unit: 'pct', multi: true,
      series: [
        { name: 'Gross', color: GREEN, values: v('grossMargin') },
        { name: 'Operating', color: GOLD, values: v('operatingMargin') },
        { name: 'Net', color: BLUE, values: v('netMargin') },
        { name: 'Expense ratio', color: RUST, values: v('expenseRatio') },
      ],
    };
  }
  if (indicator === 'inventoryTurnover') return { unit: 'times', series: [{ name: 'Inventory turnover', color: BLUE, values: v('inventoryTurnover') }] };
  if (indicator === 'revenue') return { unit: 'money', series: [{ name: 'Revenue', color: GREEN, fill: true, values: v('revenue') }] };
  if (indicator === 'netProfit') return { unit: 'money', series: [{ name: 'Net profit', color: GOLD, values: v('netProfit') }] };
  if (indicator === 'cash') return { unit: 'money', series: [{ name: 'Cash', color: BLUE, fill: true, values: v('cash') }] };
  const meta = INDICATORS.find((i) => i.key === indicator);
  return { unit: 'pct', series: [{ name: (meta && meta.label) || indicator, color: GOLD, values: v(indicator) }] };
}

function periodValue(indicator, ins) {
  const m = ins.margins, t = ins.totals;
  if (indicator === 'revenue') return fmtMoney(t.revenue);
  if (indicator === 'netProfit') return fmtMoney(t.netProfit);
  if (indicator === 'cash') return ins.monthly.length ? fmtMoney(ins.monthly[ins.monthly.length - 1].cash) : '—';
  if (indicator === 'inventoryTurnover') return fmtTimes(m.inventoryTurnover);
  if (indicator === 'margins') return 'Net ' + fmtPct(m.netMargin);
  return fmtPct(m[indicator]);
}

export function InsightsContent({ ins, compact }) {
  const [indicator, setIndicator] = useState('margins');
  const m = ins.monthly || [];
  const cats = m.map((x) => x.label);
  const hasMonths = m.length > 0;
  const trend = trendSeries(indicator, m);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <KpiCard label="Revenue" value={fmtMoney(ins.totals.revenue)} sub={'Net profit ' + fmtMoney(ins.totals.netProfit)} />
        <KpiCard label="Gross margin" value={fmtPct(ins.margins.grossMargin)} sub={'GP ' + fmtMoney(ins.totals.grossProfit)} color={healthColour(ins.margins.grossMargin)} />
        <KpiCard label="Operating margin" value={fmtPct(ins.margins.operatingMargin)} color={healthColour(ins.margins.operatingMargin)} />
        <KpiCard label="Net profit margin" value={fmtPct(ins.margins.netMargin)} color={healthColour(ins.margins.netMargin)} />
        <KpiCard label="Return on equity" value={fmtPct(ins.margins.roe)} sub="Net profit ÷ avg equity" color={healthColour(ins.margins.roe)} />
        <KpiCard label="Inventory turnover" value={fmtTimes(ins.margins.inventoryTurnover)} sub={ins.hasInventory ? 'COGS ÷ avg inventory' : 'no inventory tracked'} />
        <KpiCard label="Expense ratio" value={fmtPct(ins.margins.expenseRatio)} sub={'Opex ' + fmtMoney(ins.totals.opex)} color={healthColour(ins.margins.expenseRatio, 'expenseRatio')} />
      </div>

      {!hasMonths && <div style={{ fontSize: 12, color: '#9a9488' }}>No posted journals in this period — charts will populate once transactions are posted.</div>}

      {hasMonths && (
        <>
          <Panel
            title="Indicator trend"
            subtitle="How the selected indicator moves month to month"
            right={
              <select value={indicator} onChange={(e) => setIndicator(e.target.value)} style={{ padding: '5px 8px', fontSize: 12, border: '1px solid #e0ddd8', borderRadius: 4 }}>
                {INDICATORS.map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}
              </select>
            }
          >
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>{periodValue(indicator, ins)} <span style={{ fontSize: 11, fontWeight: 400, color: '#9a9488' }}>· period</span></div>
            <LineChart categories={cats} height={compact ? 150 : 210} series={trend.series} fmt={trend.unit === 'pct' ? TICK_PCT : trend.unit === 'times' ? TICK_TIMES : fmtMoneyShort} />
            {trend.multi && <Legend items={trend.series.map((s) => ({ name: s.name, color: s.color }))} />}
          </Panel>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
            <Panel title="Revenue vs costs" subtitle="Income against cost of sales + operating expenses">
              <BarChart fmt={fmtMoneyShort} categories={cats} height={compact ? 150 : 200} series={[
                { name: 'Revenue', color: GREEN, values: m.map((x) => x.revenue) },
                { name: 'Costs', color: RUST, values: m.map((x) => (x.cogs || 0) + (x.expenses || 0)) },
              ]} />
              <Legend items={[{ name: 'Revenue', color: GREEN }, { name: 'Costs (COGS + opex)', color: RUST }]} />
            </Panel>

            <Panel title="Profit trend" subtitle="Net profit each month, and cumulative">
              <BarChart fmt={fmtMoneyShort} categories={cats} height={compact ? 130 : 170} series={[{ name: 'Net profit', color: GOLD, values: m.map((x) => x.netProfit) }]} />
              <LineChart fmt={fmtMoneyShort} categories={cats} height={compact ? 80 : 100} series={[{ name: 'Cumulative', color: GOLD, fill: true, values: m.map((x) => x.cumulativeProfit) }]} />
            </Panel>

            {!compact && (
              <Panel title="Cash position" subtitle="Bank & cash balance at each month-end">
                <LineChart fmt={fmtMoneyShort} categories={cats} height={200} series={[{ name: 'Cash', color: BLUE, fill: true, values: m.map((x) => x.cash) }]} />
              </Panel>
            )}
            {!compact && ins.topExpenses && ins.topExpenses.length > 0 && (
              <Panel title="Top expenses" subtitle="Largest cost categories for the period">
                <HBarChart color={RUST} fmt={fmtMoney} items={ins.topExpenses.map((e) => ({ label: e.name, value: e.amount }))} />
              </Panel>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export function InsightsStrip({ coa, opening, journals }) {
  const ins = useMemo(() => {
    const js = journals || [];
    let mx = '';
    js.forEach((j) => { if (j.date && j.date > mx) mx = j.date; });
    const anchor = mx || new Date().toISOString().slice(0, 10);
    const d = new Date(anchor); d.setDate(d.getDate() - 180);
    const from = d.toISOString().slice(0, 10);
    const inPeriod = filterJournals(js, from, anchor);
    const before = js.filter((j) => j.date < from);
    const effOpening = before.length ? openingAsAt(opening || {}, before) : (opening || {});
    return computeInsights(coa || [], effOpening, inPeriod);
  }, [coa, opening, journals]);
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 10 }}>Financial health <span style={{ fontSize: 11, fontWeight: 400, color: '#9a9488' }}>· last 6 months — open the Insights tab to change period</span></div>
      <InsightsContent ins={ins} compact />
    </div>
  );
}

function FilterButton({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid ' + (active ? '#1a1a2e' : '#e0ddd8'), background: active ? '#1a1a2e' : '#fff', color: active ? '#c8a24e' : '#444', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{children}</button>
  );
}

export default function Insights({ project, coa, opening, journals }) {
  const ref = useRef(null);
  const [period, setPeriod] = useState('90');
  const [cf, setCf] = useState('');
  const [ct, setCt] = useState('');

  // Anchor rolling windows to the most recent transaction date.
  const anchor = useMemo(() => {
    let mx = '';
    (journals || []).forEach((j) => { if (j.date && j.date > mx) mx = j.date; });
    return mx || new Date().toISOString().slice(0, 10);
  }, [journals]);

  const range = rangeFor(period, project, cf, ct, anchor);
  const ins = useMemo(() => {
    const inPeriod = filterJournals(journals || [], range.from, range.to);
    const before = (journals || []).filter((j) => range.from && j.date < range.from);
    const effOpening = before.length ? openingAsAt(opening || {}, before) : (opening || {});
    return computeInsights(coa || [], effOpening, inPeriod);
  }, [coa, opening, journals, range.from, range.to]);

  const periods = [['30', 'Last 30 days'], ['60', 'Last 60 days'], ['90', 'Last 3 months'], ['year', 'This year'], ['older', 'Older'], ['all', 'All time'], ['custom', 'Custom']];

  async function exportPdf() {
    const el = ref.current; if (!el) return;
    const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#ffffff' });
    const img = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const w = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const h = (canvas.height * w) / canvas.width;
    let pos = 0, remaining = h;
    pdf.addImage(img, 'PNG', 0, pos, w, h); remaining -= pageH;
    while (remaining > 0) { pos -= pageH; pdf.addPage(); pdf.addImage(img, 'PNG', 0, pos, w, h); remaining -= pageH; }
    pdf.save((project.name || 'Company') + ' — Financial Health (' + range.label + ').pdf');
  }

  function exportXlsx() {
    const wb = XLSX.utils.book_new();
    const t = ins.totals, mar = ins.margins;
    const p = (x) => (x == null ? '' : +(x * 100).toFixed(2));
    const summary = [
      ['Financial Health', project.name || ''],
      ['Period', range.label + (range.from || range.to ? ' (' + (range.from || '…') + ' to ' + (range.to || '…') + ')' : '')],
      [],
      ['Metric', 'Value'],
      ['Revenue', t.revenue], ['Cost of sales', t.cogs], ['Gross profit', t.grossProfit], ['Gross margin %', p(mar.grossMargin)],
      ['Operating expenses', t.opex], ['Operating income', t.operatingIncome], ['Operating margin %', p(mar.operatingMargin)],
      ['Net profit', t.netProfit], ['Net profit margin %', p(mar.netMargin)], ['Expense ratio %', p(mar.expenseRatio)],
      ['Return on equity %', p(mar.roe)], ['Inventory turnover (x)', mar.inventoryTurnover == null ? '' : +mar.inventoryTurnover.toFixed(2)],
      [], ['Total assets', t.totalAssets], ['Total liabilities', t.totalLiabilities], ['Total equity', t.totalEquity],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), 'Summary');
    const monthly = [['Month', 'Revenue', 'COGS', 'Operating expenses', 'Net profit', 'Cumulative profit', 'Cash', 'Gross margin %', 'Operating margin %', 'Net margin %', 'ROE %', 'Inventory turnover']];
    ins.monthly.forEach((x) => monthly.push([x.label, x.revenue, x.cogs, x.expenses, x.netProfit, x.cumulativeProfit, x.cash, p(x.grossMargin), p(x.operatingMargin), p(x.netMargin), p(x.roe), x.inventoryTurnover == null ? '' : +x.inventoryTurnover.toFixed(2)]));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(monthly), 'Monthly');
    XLSX.writeFile(wb, (project.name || 'Company').replace(/[^\w]+/g, '_') + '_Financial_Health.xlsx');
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>Financial Health & Insights</div>
          <div style={{ fontSize: 11, color: '#9a9488', marginTop: 2 }}>Margins, ratios and trends — computed from posted journals.</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={exportXlsx} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #1a1a2e', background: '#fff', color: '#1a1a2e', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>⤓ Excel</button>
          <button onClick={exportPdf} style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: '#1a1a2e', color: '#c8a24e', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>⤓ PDF report</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        {periods.map(([k, lbl]) => <FilterButton key={k} active={period === k} onClick={() => setPeriod(k)}>{lbl}</FilterButton>)}
        {period === 'custom' && (
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginLeft: 4 }}>
            <input type="date" value={cf} onChange={(e) => setCf(e.target.value)} style={{ padding: '5px 7px', fontSize: 12, border: '1px solid #e0ddd8', borderRadius: 4 }} />
            <span style={{ color: '#9a9488', fontSize: 12 }}>to</span>
            <input type="date" value={ct} onChange={(e) => setCt(e.target.value)} style={{ padding: '5px 7px', fontSize: 12, border: '1px solid #e0ddd8', borderRadius: 4 }} />
          </span>
        )}
      </div>

      <div ref={ref} style={{ background: '#faf9f6', padding: 16, borderRadius: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', marginBottom: 2 }}>{project.name}</div>
        <div style={{ fontSize: 11, color: '#9a9488', marginBottom: 12 }}>{range.label}{(range.from || range.to) ? ' · ' + (range.from || '…') + ' to ' + (range.to || '…') : ''}</div>
        <InsightsContent ins={ins} />
      </div>
    </div>
  );
}
